/**
 * Zoom guard — auto-resets an accidental pinch-zoom back to fit.
 *
 * The native game shell never intends pinch-zoom as an interaction (the play
 * canvas isolates strokes via `touch-action: none`). On touch devices a stray
 * two-finger pinch would otherwise leave the UI "stuck" zoomed with no way back
 * to the layout that fits the screen.
 *
 * We deliberately do NOT set `user-scalable=no` (WCAG 2 AA 1.4.4 forbids
 * disabling zoom outright — see ~/.claude/rules/responsive-layout.md). Instead
 * the pinch is allowed to happen, then snapped back to scale 1 once the gesture
 * settles — momentary zoom still works for low-vision users, but it never sticks.
 *
 * Touch-only: desktop trackpad / keyboard (Cmd +/-) zoom is left untouched so
 * browser accessibility zoom on the plain web build is unaffected.
 */
export function initZoomGuard(): void {
  const vp = window.visualViewport;
  if (!vp) return;
  const isTouch =
    matchMedia('(pointer: coarse)').matches || 'ontouchstart' in window;
  if (!isTouch) return;

  const meta = document.querySelector('meta[name="viewport"]');
  if (!meta) return;
  const base =
    meta.getAttribute('content') ??
    'width=device-width, initial-scale=1, viewport-fit=cover';

  let timer: ReturnType<typeof setTimeout> | undefined;
  let resetting = false;

  const snapBack = (): void => {
    resetting = true;
    // Toggling maximum-scale forces WKWebView / Chrome to re-clamp zoom to 1.
    meta.setAttribute('content', `${base}, maximum-scale=1`);
    requestAnimationFrame(() => {
      // Restore the zoom-allowing meta so a deliberate later pinch still works.
      setTimeout(() => {
        meta.setAttribute('content', base);
        resetting = false;
      }, 300);
    });
  };

  vp.addEventListener('resize', () => {
    if (resetting) return;
    // Only act on real zoom (scale > 1); ignore keyboard / chrome resizes.
    if (vp.scale > 1.02) {
      if (timer) clearTimeout(timer);
      // Wait for the gesture to settle so we don't fight the user mid-pinch.
      timer = setTimeout(snapBack, 450);
    }
  });
}
